import { useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { xorBy } from 'lodash'
import { useEffect } from 'react'

import { AddNewProgramFormModel } from '../../../AddNewProgram.types'
import { FieldSection } from '../../../formSection/fieldSection/FieldSection'
import { FieldSubSection } from '../../../formSection/fieldSubSection/FieldSubSection'

import { AgentVerificationAssociationCheck } from '@percent/admin-dashboard/api/actions/validationSubmissions/validationSubmissions.types'
import { Checkbox, Flex, Grid, Typography } from '@percent/lemonade'

export function AgentVerification() {
  const { t } = useTranslation()

  const { setValue, control } = useFormContext<AddNewProgramFormModel>()

  const [isStatusDynamic, identityChecks, associationChecks] = useWatch({
    control,
    name: ['isStatusDynamic', 'identityChecks', 'associationChecks']
  })

  const onAssociationCheckChange = (associationCheck: AgentVerificationAssociationCheck) =>
    setValue(
      'associationChecks',
      xorBy(associationChecks, [associationCheck], item => item.type)
    )

  const isDynamicAgentVerificationStatusDisabled = !identityChecks?.length && !associationChecks?.length

  useEffect(() => {
    if (isDynamicAgentVerificationStatusDisabled) {
      setValue('isStatusDynamic', false)
    }
  }, [isDynamicAgentVerificationStatusDisabled, setValue])

  return (
    <FieldSection subSection>
      <Checkbox
        variant="default"
        active={!!(identityChecks?.length || associationChecks?.length)}
        name="agentVerification"
        label={t('form.agentVerification')}
        onChange={event => {
          const isChecked = (event as React.ChangeEvent<HTMLInputElement>).target.checked

          if (!isChecked) {
            setValue('identityChecks', [])
            setValue('associationChecks', [])
          } else {
            setValue('identityChecks', [{ type: 'inbox_verification' }])
            setValue('associationChecks', [
              { type: 'domain_matching' },
              { type: 'association_emails' },
              { type: 'manual' }
            ])
          }
        }}
      />
      <FieldSubSection>
        <Grid>
          <Grid.Col span={4}>
            <Flex direction="column" gap={8}>
              <Typography variant="default">{t('form.identityChecks')}</Typography>
              <Checkbox
                variant="default"
                active={identityChecks?.some(check => check.type === 'inbox_verification')}
                name="inboxVerification"
                label={t('form.inboxVerification')}
                onChange={() =>
                  setValue(
                    'identityChecks',
                    xorBy(identityChecks, [{ type: 'inbox_verification' as const }], item => item.type)
                  )
                }
              />
            </Flex>
          </Grid.Col>
          <Grid.Col span={4}>
            <Flex direction="column" gap={8}>
              <Typography variant="default">{t('form.associationChecks')}</Typography>
              <Checkbox
                variant="default"
                active={associationChecks?.some(check => check.type === 'domain_matching')}
                name="domainMatching"
                label={t('form.domainMatching')}
                onChange={() => onAssociationCheckChange({ type: 'domain_matching' })}
              />
              <Checkbox
                variant="default"
                active={associationChecks?.some(check => check.type === 'association_emails')}
                name="associationEmails"
                label={t('form.associationEmails')}
                onChange={() => onAssociationCheckChange({ type: 'association_emails' })}
              />
              <Checkbox
                variant="default"
                active={associationChecks?.some(check => check.type === 'manual')}
                name="manual"
                label={t('form.manual')}
                onChange={() => onAssociationCheckChange({ type: 'manual' })}
              />
            </Flex>
          </Grid.Col>
        </Grid>
      </FieldSubSection>
      <Checkbox
        variant="default"
        active={!!isStatusDynamic}
        name="dynamicStatus"
        label={t('form.dynamicAgentVerificationStatus')}
        disabled={isDynamicAgentVerificationStatusDisabled}
        onChange={() => setValue('isStatusDynamic', !isStatusDynamic)}
      />
    </FieldSection>
  )
}
